// src/disc/DiscTestBulkSend.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Divider,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CloudDone as CloudDoneIcon,
  PersonAdd as PersonAddIcon,
  TableChart as TableChartIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TestReminderFields, { DEFAULT_REMINDER_CONFIG } from '../layouts/TestReminderFields';
import useStore from '../config/store';
import api from '../config/api';
import * as XLSX from 'xlsx';

// Filtro explícito del selector de cargo: SOLO por nombre, insensible a
// mayúsculas y tildes. Usa el texto visible del campo (estado propio) para no
// depender del inputValue interno del Autocomplete.
const normalizeText = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const filterPositionsByName = (options, searchTerm) => {
  const term = normalizeText(searchTerm).trim();
  if (!term) return options;
  return options.filter((option) => normalizeText(option?.name).includes(term));
};

const DiscTestBulkSend = ({ applicants }) => {
  const { t } = useTranslation();
  const { authToken, project, organization, user, showAlert, language } = useStore();

  // Estados del modo de selección (PRINCIPAL: manual, SECUNDARIO: excel)
  const [selectionMode, setSelectionMode] = useState('manual');

  // Estados del selector manual (MULTISELECT)
  const [selectedApplicants, setSelectedApplicants] = useState([]);

  // Estados del archivo Excel
  const [file, setFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Estados del envío
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  // Estados de configuración del test
  const [testMode, setTestMode] = useState('simple');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positionSearchText, setPositionSearchText] = useState('');
  const [currentPosition, setCurrentPosition] = useState('');
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [reminderConfig, setReminderConfig] = useState(DEFAULT_REMINDER_CONFIG);

  // Estados de datos
  const [positions, setPositions] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);

  // Competencias predeterminadas del cargo (2026-09-09): al cambiar el cargo, la
  // selección se REEMPLAZA por las predeterminadas de ese cargo (o queda vacía si
  // no tiene / no hay cargo). Lo que el reclutador agregue o quite después aplica
  // solo a este envío; nunca modifica el cargo. Se resuelve contra el catálogo
  // cargado para reutilizar los mismos objetos del Autocomplete.
  const handlePositionChange = (newPosition) => {
    setSelectedPosition(newPosition);
    const defaults = Array.isArray(newPosition?.competencias) ? newPosition.competencias : [];
    setSelectedCompetencies(
      defaults
        .map((c) => {
          const id = c.competencyId ?? c.id ?? c.uid;
          const fromCatalog = competencies.find((opt) => (opt.id ?? opt.uid) === id);
          return fromCatalog || (id ? { id, name: c.competencyName ?? c.name ?? '' } : null);
        })
        .filter(Boolean)
    );
  };

  const pollingIntervalRef = useRef(null);

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Polling para job status
  useEffect(() => {
    if (bulkJobId && sendingBulk) {
      const intervalId = setInterval(() => {
        checkJobStatus(bulkJobId);
      }, 3000);
      pollingIntervalRef.current = intervalId;
      return () => clearInterval(intervalId);
    }
  }, [bulkJobId, sendingBulk]);

  // Cargar cargos
  useEffect(() => {
    const loadPositions = async () => {
      // El endpoint /positions exige projectId; sin él responde error y el
      // selector de cargo quedaba vacío en modo completo.
      if (!authToken || !project) return;
      try {
        setLoadingPositions(true);
        const response = await api.get('/positions', {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data.errorCode === 0) {
          setPositions(response.data.response.data || []);
        }
      } catch (error) {
        console.error('Error loading positions:', error);
      } finally {
        setLoadingPositions(false);
      }
    };

    if (testMode === 'complete') {
      loadPositions();
    }
  }, [authToken, testMode, project]);

  // Cargar competencias
  useEffect(() => {
    const loadCompetencies = async () => {
      if (!authToken || !language || !organization) return;
      try {
        setLoadingCompetencies(true);
        const response = await api.get(
          `/capabilities`,
          {
            params: {
              language,
              organizationId: organization
            },
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );
        if (response.data.errorCode === 0) {
          setCompetencies(response.data.response.data || []);
        }
      } catch (error) {
        console.error('Error loading competencies:', error);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    if (testMode === 'complete') {
      loadCompetencies();
    }
  }, [authToken, language, testMode, organization]);

  // ========================================
  // FUNCIONES PARA EXCEL
  // ========================================

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(selectedFile.type)) {
        setFileError(t('disc.bulkSend.fileUpload.invalidFileType'));
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setFileError(null);
      processExcelFile(selectedFile);
    }
  };

  const processExcelFile = (file) => {
    setLoadingFile(true);
    setFileError(null);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setFileError(t('disc.bulkSend.fileUpload.emptyFile'));
          setLoadingFile(false);
          return;
        }

        // Extraer emails del Excel
        const emails = jsonData
          .map(row => {
            const emailKey = Object.keys(row).find(key =>
              key.toLowerCase() === 'email'
            );
            return emailKey ? row[emailKey] : null;
          })
          .filter(email => email && email.trim() !== '');

        if (emails.length === 0) {
          setFileError(t('disc.bulkSend.fileUpload.noEmailColumn'));
          setLoadingFile(false);
          return;
        }

        // Mapear emails con applicants existentes
        const matchedApplicants = [];
        const unmatchedEmails = [];

        emails.forEach(email => {
          const applicant = applicants.find(
            a => a.email && a.email.toLowerCase() === email.toLowerCase()
          );
          if (applicant) {
            matchedApplicants.push(applicant);
          } else {
            unmatchedEmails.push(email);
          }
        });

        setPreviewData({
          totalEmails: emails.length,
          matched: matchedApplicants,
          unmatched: unmatchedEmails
        });

      } catch (error) {
        console.error('Error processing file:', error);
        setFileError(t('disc.bulkSend.fileUpload.processError') + ' ' + error.message);
      } finally {
        setLoadingFile(false);
      }
    };

    reader.onerror = () => {
      setFileError(t('disc.bulkSend.fileUpload.readError'));
      setLoadingFile(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const template = [
      { email: "juan.perez@example.com" },
      { email: "maria.garcia@example.com" },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidatos");
    XLSX.writeFile(wb, "plantilla_envio_masivo_disc.xlsx");
  };

  // ========================================
  // FUNCIONES PARA ENVÍO
  // ========================================

  const getAllSelectedApplicants = () => {
    // Combinar selección manual + Excel (sin duplicados)
    const fromExcel = previewData?.matched || [];
    const combined = [...selectedApplicants];

    fromExcel.forEach(excelApp => {
      if (!combined.some(a => a.id === excelApp.id)) {
        combined.push(excelApp);
      }
    });

    return combined;
  };

  const isBulkFormValid = () => {
    const allSelected = getAllSelectedApplicants();
    return allSelected.length > 0;
  };

  const handleSendBulk = async () => {
    if (!isBulkFormValid()) {
      showAlert(t('disc.bulkSend.messages.loadValidCandidates'), 'warning');
      return;
    }

    const allSelected = getAllSelectedApplicants();

    try {
      setSendingBulk(true);
      setFileError(null);

      const bulkData = {
        applicants: allSelected.map(a => ({
          id: a.id || a.uid,
          name: a.name,
          email: a.email
        })),
        mode: testMode,
        projectId: project,
        organizationId: organization,
        userId: user.uid,
        userEmail: user.email,
        language: language || 'es',
        sendEmail,
        reminderConfig,
      };

      // Agregar datos opcionales si el modo es completo
      if (testMode === 'complete') {
        if (selectedPosition) {
          bulkData.positionId = selectedPosition.id ?? selectedPosition.uid;
          bulkData.positionName = selectedPosition.name;
          bulkData.positionDescription = selectedPosition.descripcion?.trim() || '';
        }
        if (currentPosition.trim()) {
          bulkData.currentPosition = currentPosition.trim();
        }
        if (selectedCompetencies.length > 0) {
          bulkData.competencies = selectedCompetencies.map(c => ({
            competencyId: c.id ?? c.uid,
            competencyName: c.name ?? '',
          }));
        }
      }

      const response = await api.post(
        '/disc-tests/bulk',
        bulkData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.errorCode === 0) {
        const jobId = response.data.response.data.jobId;
        setBulkJobId(jobId);
        showAlert(
          `${t('disc.bulkSend.messages.bulkStarted')} ${allSelected.length} ${t('disc.bulkSend.actions.candidates')}`,
          'success'
        );
      } else {
        throw new Error(response.data.errorMessage || 'Error al iniciar bulk');
      }

    } catch (error) {
      console.error('Error sending bulk:', error);
      showAlert(t('disc.bulkSend.messages.errorStartingBulk'), 'error');
      setSendingBulk(false);
    }
  };

  const checkJobStatus = async (jobId) => {
    try {
      const response = await api.get(
        `/disc-tests/bulk/${jobId}/status`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.errorCode === 0) {
        const status = response.data.response;
        setJobStatus(status);

        if (status.status === 'completed' || status.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setSendingBulk(false);

          if (status.status === 'completed') {
            showAlert(
              `${t('disc.bulkSend.messages.sendCompleted')} ${status.results?.succeeded || 0} ${t('disc.bulkSend.messages.successful')}, ${status.results?.failed || 0} ${t('disc.bulkSend.messages.failed')}`,
              'success'
            );
            resetForm();
          } else {
            showAlert(t('disc.bulkSend.messages.bulkFailed'), 'error');
          }
        }
      }
    } catch (error) {
      console.error('Error checking job status:', error);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreviewData(null);
    setFileError(null);
    setBulkJobId(null);
    setJobStatus(null);
    setTestMode('simple');
    setSelectedPosition(null);
    setCurrentPosition('');
    setSelectedCompetencies([]);
    setSelectedApplicants([]);
    setSendEmail(true);
    setReminderConfig(DEFAULT_REMINDER_CONFIG);
  };

  // ========================================
  // RENDER HELPERS
  // ========================================

  const renderSelectionModeToggle = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('disc.bulkSend.selectionMethod')}
      </Typography>
      <ToggleButtonGroup
        value={selectionMode}
        exclusive
        onChange={(e, newMode) => {
          if (newMode !== null) {
            setSelectionMode(newMode);
          }
        }}
        fullWidth
      >
        <ToggleButton value="manual">
          <PersonAddIcon sx={{ mr: 1 }} />
          {t('disc.bulkSend.selectManually')}
        </ToggleButton>
        <ToggleButton value="excel">
          <TableChartIcon sx={{ mr: 1 }} />
          {t('disc.bulkSend.uploadExcel')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const renderManualSelector = () => (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        multiple
        options={applicants}
        getOptionLabel={(option) => `${option.name} (${option.email})`}
        value={selectedApplicants}
        onChange={(event, newValue) => setSelectedApplicants(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('disc.bulkSend.selectApplicants')}
            placeholder={t('disc.bulkSend.searchApplicants')}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              label={option.name}
              {...getTagProps({ index })}
              color="primary"
            />
          ))
        }
        noOptionsText={t('disc.bulkSend.noApplicantsFound')}
      />

      {selectedApplicants.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('disc.bulkSend.applicantsSelected', { count: selectedApplicants.length })}
        </Alert>
      )}
    </Box>
  );

  const renderExcelUpload = () => (
    <Box sx={{ mb: 3 }}>
      {/* Instrucciones */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>{t('disc.bulkSend.instructions.title')}</strong>
        </Typography>
        <Typography variant="body2" component="div">
          1. {t('disc.bulkSend.instructions.step1')}<br />
          2. {t('disc.bulkSend.instructions.step2')}<br />
          3. {t('disc.bulkSend.instructions.step3')}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={downloadTemplate}
          sx={{ mt: 1 }}
        >
          {t('disc.bulkSend.instructions.downloadTemplate')}
        </Button>
      </Alert>

      {/* Zona de carga */}
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: fileError ? 'error.main' : 'divider',
          backgroundColor: loadingFile ? 'action.hover' : 'background.paper',
          cursor: loadingFile ? 'wait' : 'pointer',
          '&:hover': {
            borderColor: loadingFile ? 'divider' : 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => !loadingFile && document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={loadingFile}
        />

        {loadingFile ? (
          <Box>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body2">
              {t('disc.bulkSend.fileUpload.processing')}
            </Typography>
          </Box>
        ) : file ? (
          <Box>
            <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {file.name}
            </Typography>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setPreviewData(null);
                setFileError(null);
              }}
            >
              {t('remove')}
            </Button>
          </Box>
        ) : (
          <Box>
            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {t('disc.bulkSend.fileUpload.dragOrClick')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('disc.bulkSend.fileUpload.supportedFormats')}
            </Typography>
          </Box>
        )}
      </Paper>

      {fileError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fileError}
        </Alert>
      )}
    </Box>
  );

  const renderPreview = () => {
    const allSelected = getAllSelectedApplicants();

    if (allSelected.length === 0) {
      return null;
    }

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('disc.bulkSend.preview.summary')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('disc.bulkSend.preview.candidatesFound')}:</strong> {allSelected.length}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          {t('disc.bulkSend.preview.candidatesList')}
        </Typography>

        <List dense>
          {allSelected.slice(0, 5).map((applicant, index) => (
            <ListItem key={applicant.id || index} disableGutters>
              <ListItemText
                primary={applicant.name}
                secondary={applicant.email}
              />
            </ListItem>
          ))}
          {allSelected.length > 5 && (
            <ListItem disableGutters>
              <ListItemText
                secondary={`... y ${allSelected.length - 5} más`}
              />
            </ListItem>
          )}
        </List>

        {previewData?.unmatched?.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('disc.bulkSend.preview.unmatchedEmails', { count: previewData.unmatched.length })}
            </Typography>
          </Alert>
        )}
      </Paper>
    );
  };

  const renderConfiguration = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('disc.bulkSend.configuration.title')}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('disc.testMode')}</InputLabel>
        <Select
          value={testMode}
          label={t('disc.testMode')}
          onChange={(e) => setTestMode(e.target.value)}
        >
          <MenuItem value="simple">{t('disc.simpleMode')}</MenuItem>
          <MenuItem value="complete">{t('disc.completeMode')}</MenuItem>
        </Select>
      </FormControl>

      {testMode === 'complete' && (
        <>
          <Autocomplete
            options={positions}
            getOptionLabel={(option) => option.name || ''}
            getOptionKey={(option) => option.uid ?? option.id}
            isOptionEqualToValue={(option, value) =>
              (option.uid ?? option.id) === (value.uid ?? value.id)
            }
            filterOptions={(opts) => filterPositionsByName(opts, positionSearchText)}
            onInputChange={(event, newInputValue) => setPositionSearchText(newInputValue)}
            value={selectedPosition}
            onChange={(event, newValue) => handlePositionChange(newValue)}
            renderOption={(props, option) => (
              // key única por uid: con nombres duplicados, la key por defecto
              // (el nombre) rompe el render de la lista al filtrar.
              <li {...props} key={option.uid ?? option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('disc.position')}
                placeholder={t('disc.selectPosition')}
              />
            )}
            loading={loadingPositions}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={t('disc.currentPosition')}
            value={currentPosition}
            onChange={(e) => setCurrentPosition(e.target.value)}
            placeholder={t('disc.currentPositionPlaceholder')}
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            options={competencies}
            getOptionLabel={(option) => option.name || option.capabilityName || ''}
            value={selectedCompetencies}
            onChange={(event, newValue) => setSelectedCompetencies(newValue)}
            isOptionEqualToValue={(option, value) => (option.id ?? option.uid) === (value.id ?? value.uid)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('disc.selectCompetencies')}
                placeholder={t('disc.competenciesDescription')}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option.name || option.capabilityName}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            loading={loadingCompetencies}
          />
        </>
      )}

      {/* Switch enviar email (mismo patrón que el envío individual) */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
        <EmailIcon sx={{ color: sendEmail ? 'primary.main' : 'action.disabled' }} />
        <FormControlLabel
          control={
            <Switch
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                {t('disc.sendEmailOnCreate')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {sendEmail
                  ? t('disc.sendEmailOnCreateEnabled')
                  : t('disc.sendEmailOnCreateDisabled')
                }
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* Recordatorio automatico: solo aplica cuando el correo lo manda el sistema */}
      <TestReminderFields
        show={sendEmail}
        value={reminderConfig}
        onChange={setReminderConfig}
      />
    </Paper>
  );

  const renderProgress = () => {
    if (!jobStatus) return null;

    const progress = jobStatus.results
      ? (jobStatus.results.succeeded + jobStatus.results.failed) / jobStatus.total * 100
      : 0;

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('disc.bulkSend.progress.title')}
        </Typography>

        <LinearProgress variant="determinate" value={progress} sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            {t('disc.bulkSend.progress.processed')}: {(jobStatus.results?.succeeded || 0) + (jobStatus.results?.failed || 0)} / {jobStatus.total}
          </Typography>
          <Typography variant="body2">
            {t('disc.bulkSend.progress.successful')}: {jobStatus.results?.succeeded || 0}
          </Typography>
          <Typography variant="body2">
            {t('disc.bulkSend.progress.failed')}: {jobStatus.results?.failed || 0}
          </Typography>
        </Box>
      </Paper>
    );
  };

  // ========================================
  // RENDER PRINCIPAL
  // ========================================

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('disc.bulkSend.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('disc.bulkSend.subtitle')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Toggle entre Manual y Excel */}
      {renderSelectionModeToggle()}

      {/* Modo Manual - PRINCIPAL */}
      {selectionMode === 'manual' && renderManualSelector()}

      {/* Modo Excel - SECUNDARIO */}
      {selectionMode === 'excel' && renderExcelUpload()}

      {/* Preview combinado */}
      {renderPreview()}

      {/* Configuración del test */}
      {renderConfiguration()}

      {/* Progreso del envío */}
      {renderProgress()}

      {/* Botones de acción */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={resetForm}
          disabled={sendingBulk}
        >
          {t('disc.bulkSend.actions.cancel')}
        </Button>

        <Button
          variant="contained"
          onClick={handleSendBulk}
          disabled={!isBulkFormValid() || sendingBulk}
          startIcon={sendingBulk ? <CircularProgress size={16} /> : <UploadIcon />}
        >
          {sendingBulk
            ? t('disc.bulkSend.actions.sending')
            : sendEmail
              ? `${t('disc.bulkSend.actions.sendTo')} ${getAllSelectedApplicants().length} ${t('disc.bulkSend.actions.candidates')}`
              : `${t('disc.createTest')} (${getAllSelectedApplicants().length})`
          }
        </Button>
      </Box>
    </Paper>
  );
};

export default DiscTestBulkSend;